import {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry,
  GeometryCollection
} from "geojson";

import { ALL_COLORS, COLORS } from "@/constants/colors";
/**
 * Converts a DatasetResponse to a GeoJSON FeatureCollection.
 * Each row in the dataset becomes a feature, where the properties are mapped from the columns.
 * If attributesSchema is provided, it's used for mapping; otherwise, response.columns is used.
 */
import {
  CATCHMENT_GRID_ID_COLUMN_NAME,
  DATA_TYPE_VALUES,
  DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
  DEFAULT_SINGLE_STYLE_FILL_COLOR,
  DEFAULT_SINGLE_STYLE_OUTLINE_COLOR,
  MAX_STEP,
  MIN_STEP,
  ORDINAL_TYPES,
  ORDINAL_TYPES_COLUMN,
  SPATIAL_AGGREGATION_NAME,
  SPATIAL_ANALYSIS_RESULT_DATASET_NAME,
  SPATIAL_TYPE_TO_LAYER_TYPE
} from "@/constants/dataset";
import type { Timezone } from "@/constants/dateTime";
import { GEOMETRY_SOURCE_PROPERTY, UNIQUE_ID_PROPERTY } from "@/constants/map";
import { normalizeFilterValue } from "@/lib/utils";
import { defaultDataset } from "@/mocks/apis/dataset/data";
import { SpatialAggregationType } from "@/types/common";
import {
  AddUpdateRowsDatasetPayload,
  ATTRIBUTE_ID,
  ColorRanges,
  ColorScaleMethod,
  ColorScaleSettings,
  CreateNewDatasetType,
  CustomCategoryStep,
  CustomCategoryValue,
  CustomRangeStep,
  CustomRangeValue,
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetDataColumn,
  DatasetDataRowV2,
  DatasetDataRowValue,
  DatasetDataType,
  DatasetMetadata,
  DatasetMetadataStream,
  DatasetsMap,
  EColorLabel,
  EColorNames,
  H3Configuration,
  SchemaToDatasetMap,
  SingleStyleValue,
  TransformedProperty
} from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import { CatchmentDatasetConfig } from "@/types/spatial";
import { isDatetime, translateToUserTimezone } from "@/utils/date";

import { area } from "@turf/turf";

import { normalizeAttributeValue, processValue } from "./attributeProcessing";
import { getColorScaleMap } from "./color";
import { handleCategoricalValues, handleNumericValues } from "./common";
import { convertFormatterDayjs } from "./date";
import { formatNumber } from "./number";
import { computeStepValue } from "./stepValue";
import { formatRenderValue } from "./string";
import { isWKTPoint, wktToGeoJSON } from "./wkt";

interface ProcessedRowData {
  properties: Record<string, string | number | boolean | null | undefined>;
  baseId: number | string | undefined;
  collectedGeometries: Array<{
    geometry: Geometry;
    sourceColumnName: string;
    columnIndex: number;
  }>;
}
interface ResolvedColumn {
  info: DatasetAttributeSchema | DatasetDataColumn | undefined;
  isVisible: boolean;
  isEnhancedGeo: boolean;
}

/** A column of the source rows, paired with the name its values are stored under. */
interface FeatureGeoColumn {
  index: number;
  name: string;
}

const getColumnName = (
  columnInfo: DatasetAttributeSchema | DatasetDataColumn,
  useAttributesSchema: boolean
) =>
  useAttributesSchema
    ? (columnInfo as DatasetAttributeSchema).attribute_name
    : (columnInfo as DatasetDataColumn).name;

const getIsReferenceId = (
  columnInfo: DatasetAttributeSchema | DatasetDataColumn,
  columnName: string,
  useAttributesSchema: boolean
) =>
  useAttributesSchema
    ? (columnInfo as DatasetAttributeSchema).is_reference_id ||
      columnName === "id"
    : columnName === "id";

const handleReferenceId = (
  value: DatasetDataRowValue,
  columnName: string,
  columnIndex: number,
  processedData: ProcessedRowData
) => {
  let idValue: string | number | undefined;

  if (typeof value === "string" || typeof value === "number") {
    idValue = value;
  } else if (value !== null && value !== undefined) {
    idValue = String(idValue);
  }

  processedData.baseId = idValue ?? columnIndex.toString();
  processedData.properties[columnName] = processedData.baseId;
};

/**
 * The columns that would have produced a geometry for this collection: flagged
 * `is_reference_geo` on the schema or the column, and visible. A hidden column
 * is skipped by processRow, so its WKT never reached any feature.
 */
const collectGeoColumns = (
  resolvedColumns: ResolvedColumn[],
  useAttributesSchema: boolean
): FeatureGeoColumn[] =>
  resolvedColumns.flatMap((resolved, index) =>
    resolved.isVisible && resolved.info && resolved.isEnhancedGeo
      ? [{ index, name: getColumnName(resolved.info, useAttributesSchema) }]
      : []
  );

/**
 * The columns handleReferenceId writes the feature's reference id from, in
 * column order. Their property name is the same one the value is stored under,
 * so the clicked feature carries the value to match a row on.
 */
const collectReferenceIdColumns = (
  resolvedColumns: ResolvedColumn[],
  useAttributesSchema: boolean
): FeatureGeoColumn[] =>
  resolvedColumns.flatMap((resolved, index) => {
    if (!resolved.isVisible || !resolved.info) return [];
    const name = getColumnName(resolved.info, useAttributesSchema);
    return getIsReferenceId(resolved.info, name, useAttributesSchema)
      ? [{ index, name }]
      : [];
  });

/**
 * The row the clicked feature was built from, matched on the reference id value it
 * carries. `unique_id` (the promoted feature index) only stands in for the row index
 * when there is one geo column, where every row yields exactly one feature.
 */
const findFeatureRow = (
  rows: DatasetDataRowV2[],
  featureProperties: GeoJsonProperties,
  referenceIdColumns: FeatureGeoColumn[],
  singleGeoColumn: boolean
): DatasetDataRowV2 | undefined => {
  for (const { index, name } of referenceIdColumns) {
    const referenceId = featureProperties?.[name];
    if (referenceId === undefined || referenceId === null) continue;

    const row = rows.find(
      (current) => String(current[index]) === String(referenceId)
    );
    if (row) return row;
  }

  if (referenceIdColumns.length || !singleGeoColumn) return undefined;

  // Only sound for this converter's output with nothing filtered or reordered.
  // The layers that do reorder (useSiteProfilingLayers, useCatchmentArea) register
  // under a source id that is not a `rawDatasets` key, so they never land here.
  const uniqueId = featureProperties?.[UNIQUE_ID_PROPERTY];
  return typeof uniqueId === "number" && Number.isInteger(uniqueId)
    ? rows[uniqueId]
    : undefined;
};

/**
 * Which geo column the clicked feature was built from. `Geometry Source` names it
 * exactly — the only way to tell two geometries of the same row apart — so without
 * it only a single-geo-column dataset can be resolved.
 */
const resolveGeoColumn = (
  geoColumns: FeatureGeoColumn[],
  columns: DatasetDataColumn[],
  geometrySource: unknown
): FeatureGeoColumn | undefined => {
  if (typeof geometrySource !== "string" || !geometrySource) {
    return geoColumns.length === 1 ? geoColumns[0] : undefined;
  }

  // Match the column name too: a collection built without the schema names the
  // source after the column, not after the (possibly renamed) schema attribute.
  return geoColumns.find(
    (geoColumn) =>
      geoColumn.name === geometrySource ||
      columns[geoColumn.index].name === geometrySource
  );
};

/**
 * Finds a feature's exact WKT from dataset rows, avoiding MapLibre's
 * tile-clipped geometry and unnecessary WKT payloads on features.
 *
 * Resolves off the clicked feature's properties, not its id: `promoteId: "unique_id"`
 * means the id from `queryRenderedFeatures` is the feature index, never the
 * `${baseId}-${geometryIndex}` one createFeaturesFromRowData assigns. Columns are
 * resolved through the same helpers the converter used, so the two cannot drift.
 */
export const resolveFeatureGeometryWKT = (
  datasetData: DatasetData | undefined,
  featureProperties: GeoJsonProperties | undefined,
  attributesSchema?: DatasetAttributeSchema[]
): { columnName: string; columnIndex: number; wkt: string } | null => {
  if (!datasetData || !featureProperties) return null;

  const spatialType = datasetData.spatial_aggregation_type;
  const carriesWKT =
    spatialType === SPATIAL_AGGREGATION_NAME.line.toLowerCase() ||
    spatialType === SPATIAL_AGGREGATION_NAME.polygon.toLowerCase();
  if (!carriesWKT) return null;

  const { columns, rows } = datasetData;
  if (!rows?.length) return null;

  const useAttributesSchema = !!attributesSchema;
  const resolvedColumns = preComputeColumnMetadata(
    columns,
    useAttributesSchema
      ? matchColumnsToSchema(columns, attributesSchema)
      : new Map<number, DatasetAttributeSchema>(),
    useAttributesSchema
  );

  const geoColumns = collectGeoColumns(resolvedColumns, useAttributesSchema);
  if (!geoColumns.length) return null;

  const geoColumn = resolveGeoColumn(
    geoColumns,
    columns,
    featureProperties[GEOMETRY_SOURCE_PROPERTY]
  );
  if (!geoColumn) return null;

  const row = findFeatureRow(
    rows,
    featureProperties,
    collectReferenceIdColumns(resolvedColumns, useAttributesSchema),
    geoColumns.length === 1
  );
  if (!row) return null;

  const value = row[geoColumn.index];
  if (typeof value !== "string" || !value) return null;

  return {
    columnName: geoColumn.name,
    columnIndex: geoColumn.index,
    wkt: value
  };
};

/**
 * Keeps the geometry entry in its original column order. Since geometry is now
 * resolved separately, insert it before the first attribute from a later column
 * instead of always appending it.
 */
export const getGeometryAttributeInsertIndex = (
  attributes: [string, unknown][],
  columns: DatasetData["columns"],
  geometryColumnIndex: number
): number => {
  const columnOrder = new Map(
    columns.map((column, index) => [column.name, index])
  );

  const insertAt = attributes.findIndex(
    ([key]) =>
      (columnOrder.get(key) ?? Number.POSITIVE_INFINITY) > geometryColumnIndex
  );

  return insertAt === -1 ? attributes.length : insertAt;
};

const handleGeometryColumn = (
  value: string,
  columnName: string,
  columnIndex: number,
  processedData: ProcessedRowData,
  spatialAggregationType?: SpatialAggregationType
) => {
  // Skip non-point geometries when spatial aggregation type is 'poi'
  if (spatialAggregationType === "poi" && !isWKTPoint(value)) {
    return;
  }

  if (
    spatialAggregationType === SPATIAL_AGGREGATION_NAME.H3 ||
    spatialAggregationType === SPATIAL_AGGREGATION_NAME.geohash.toLowerCase()
  ) {
    processedData.properties[columnName] = value;
    return;
  }

  // Line/Polygon rows deliberately do NOT keep the raw WKT in properties. On a
  // boundary dataset it is ~12 KB per row (measured: 257 MB over 11k rows), and
  // it rides along in everything that serializes this collection — every
  // postMessage into a MapLibre worker, every redux-persist write — while the
  // map renders from the parsed geometry below and never reads the string.
  // Feature info loses that one (12 KB) row, matching what POI already does.
  const geometry = wktToGeoJSON(value)?.geometry;
  if (geometry) {
    processedData.collectedGeometries.push({
      geometry,
      sourceColumnName: columnName,
      columnIndex
    });
  }
};

const processRowValue = (
  value: DatasetDataRowValue,
  columnInfo: DatasetAttributeSchema | DatasetDataColumn | undefined,
  useAttributesSchema: boolean,
  columnIndex: number,
  processedData: ProcessedRowData,
  spatialAggregationType?: SpatialAggregationType
) => {
  if (!columnInfo) {
    return;
  }

  const columnName = getColumnName(columnInfo, useAttributesSchema);
  const isReferenceId = getIsReferenceId(
    columnInfo,
    columnName,
    useAttributesSchema
  );

  if (isReferenceId) {
    handleReferenceId(value, columnName, columnIndex, processedData);
    return;
  }

  if (columnInfo.is_reference_geo) {
    if (typeof value === "string" && value) {
      handleGeometryColumn(
        value,
        columnName,
        columnIndex,
        processedData,
        spatialAggregationType
      );
    }
    return;
  }

  processValue(
    value,
    columnInfo as DatasetDataColumn,
    processedData.properties
  );
};

const createFeaturesFromRowData = (
  processedData: ProcessedRowData,
  rowIndex: number,
  features: Array<Feature<Geometry>>
) => {
  const { baseId, collectedGeometries, properties } = processedData;

  if (collectedGeometries.length > 0) {
    collectedGeometries.forEach(
      ({ geometry, sourceColumnName, columnIndex }, geometryIndex) => {
        const featureId =
          baseId !== undefined
            ? `${baseId}-${geometryIndex}`
            : `${rowIndex}-${columnIndex}-${geometryIndex}`;

        features.push({
          type: "Feature",
          id: featureId,
          geometry: geometry,
          properties: {
            ...properties,
            [GEOMETRY_SOURCE_PROPERTY]: sourceColumnName
          }
        });
      }
    );
  } else {
    const featureId = baseId ?? rowIndex.toString();
    features.push({
      type: "Feature",
      id: featureId,
      geometry: null as unknown as Geometry,
      properties: properties
    });
  }
};

/**
 * Builds two lookup maps from an `attributesSchema` array for efficient
 * column-to-schema matching.
 *
 * Two maps are produced:
 * - `schemaByName`     – keyed by `attribute_name` (1-to-1, last-write wins
 *                        when names collide).
 * - `schemaBySchemaId` – keyed by `id`, storing an **array** of entries
 *                        because multiple admin-area attributes can share the
 *                        same `id` (e.g. Village Name / Village Code both
 *                        reference `id: "ae95ab68-…"`).
 *
 * @param attributesSchema - The dataset schema entries returned by the API.
 * @returns An object containing both lookup maps.
 */
const buildSchemaLookupMaps = (
  attributesSchema: DatasetAttributeSchema[]
): {
  schemaByName: Map<string, DatasetAttributeSchema>;
  schemaBySchemaId: Map<string, DatasetAttributeSchema[]>;
} => {
  const schemaByName = new Map<string, DatasetAttributeSchema>();
  const schemaBySchemaId = new Map<string, DatasetAttributeSchema[]>();

  for (const attr of attributesSchema) {
    if (attr.attribute_name) {
      schemaByName.set(attr.attribute_name, attr);
    }
    if (attr.id) {
      const existing = schemaBySchemaId.get(attr.id);
      if (existing) {
        existing.push(attr);
      } else {
        schemaBySchemaId.set(attr.id, [attr]);
      }
    }
  }

  return { schemaByName, schemaBySchemaId };
};

/**
 * Attempts to find a matching `DatasetAttributeSchema` for a column by
 * comparing `col.schema_id` against the schema `id` lookup map.
 *
 * This is the **Priority 2 fallback** used when name-based matching fails
 * (e.g. a column was renamed in the UI but still carries the original
 * `schema_id`).
 *
 * When multiple schema entries share the same `id` (duplicate `schema_id`),
 * the first entry whose `position` has **not yet been claimed** is returned.
 * This prevents two columns from incorrectly resolving to the same schema row.
 *
 * @param col             - The dataset column to find a match for.
 * @param schemaBySchemaId - Lookup map of schema `id` → schema entries (may contain duplicates).
 * @param usedPositions   - Set of schema `position` values already claimed by earlier columns.
 * @returns The matched schema entry, or `undefined` if no match is found.
 */
const findBySchemaId = (
  col: DatasetDataColumn,
  schemaBySchemaId: Map<string, DatasetAttributeSchema[]>,
  usedPositions: Set<number>
): DatasetAttributeSchema | undefined => {
  if (!col.schema_id) return undefined;
  const candidates = schemaBySchemaId.get(col.schema_id);
  return candidates?.find((c) => !usedPositions.has(c.position));
};

/**
 * Creates a mapping from each **column index** (matching the row data array)
 * to its corresponding `DatasetAttributeSchema` entry.
 *
 * Matching strategy (per column, in priority order):
 * 1. **Name match** – `col.name === schema.attribute_name` (most reliable).
 * 2. **Schema-ID match** – `col.schema_id === schema.id` (handles renamed columns).
 *
 * Columns that exist in `data.rows` but have **no corresponding schema entry**
 * (e.g. `orbis_li_id`) are simply omitted from the map, so they will be
 * skipped during row processing without shifting the indices of other columns.
 *
 * A `usedSchemaPositions` set prevents the same schema entry from being
 * claimed by more than one column when `schema_id` values are duplicated
 * across admin-area attributes.
 *
 * @param columns          - The `columns` array from the dataset response.
 * @param attributesSchema - The full schema array from the API.
 * @returns A map of `columnIndex → DatasetAttributeSchema`.
 */
const matchColumnsToSchema = (
  columns: DatasetDataColumn[],
  attributesSchema: DatasetAttributeSchema[]
): Map<number, DatasetAttributeSchema> => {
  const schemaByColumnIndex = new Map<number, DatasetAttributeSchema>();
  const { schemaByName, schemaBySchemaId } =
    buildSchemaLookupMaps(attributesSchema);
  const usedSchemaPositions = new Set<number>();

  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    const matched =
      schemaByName.get(col.name) ??
      findBySchemaId(col, schemaBySchemaId, usedSchemaPositions);

    if (matched) {
      usedSchemaPositions.add(matched.position);
      schemaByColumnIndex.set(i, matched);
    }
  }

  return schemaByColumnIndex;
};

/**
 * Resolves the metadata for a single column when `attributesSchema` is being used.
 *
 * - If the schema entry is missing or has `is_visible: false`, the column is
 *   marked as not visible and will be skipped during row processing.
 * - `isEnhancedGeo` is pre-computed here (once per column, not per row) to
 *   avoid redundant checks inside the hot row loop. It is `true` when either
 *   `schema.is_reference_geo` **or** `col.is_reference_geo` is set.
 *
 * Note: we intentionally do NOT fall back to `col.data_type === TYPE_GEOMETRY`
 * because some datasets contain geometry columns that are deliberately excluded
 * from the map (e.g. secondary/unused geometry columns where both schema and
 * column have `is_reference_geo: false`). Checking `data_type` would force
 * those unwanted geometries onto the map.
 *
 * @param col    - The column definition from the dataset response.
 * @param schema - The matched schema entry (may be `undefined` if no match was found).
 * @returns A `ResolvedColumn` with pre-computed visibility and geometry flags.
 */
const resolveColumnWithSchema = (
  col: DatasetDataColumn,
  schema: DatasetAttributeSchema | undefined
): ResolvedColumn => {
  if (!schema?.is_visible) {
    return { info: undefined, isVisible: false, isEnhancedGeo: false };
  }

  // Only trust the explicit is_reference_geo flag from either source.
  // Do NOT fall back to data_type check — it would incorrectly promote
  // unused geometry columns (where is_reference_geo is intentionally false).
  const isEnhancedGeo = schema.is_reference_geo || col?.is_reference_geo;

  return { info: schema, isVisible: true, isEnhancedGeo };
};

/**
 * Pre-computes an array of `ResolvedColumn` metadata — one entry per column index.
 *
 * This runs **once before the row loop**, so that the inner per-cell loop only
 * needs a cheap array access and boolean check instead of repeated `Map.get()`
 * lookups, visibility checks, and geometry-type comparisons on every cell.
 *
 * When `useAttributesSchema` is `false`, each column is resolved directly from
 * the `columns` array with no schema involvement.
 *
 * @param columns              - The `columns` array from the dataset response.
 * @param schemaByColumnIndex  - The column-index-to-schema map built by `matchColumnsToSchema`.
 * @param useAttributesSchema  - Whether an `attributesSchema` was provided.
 * @returns An array aligned with the column indices, each containing pre-resolved metadata.
 */
const preComputeColumnMetadata = (
  columns: DatasetDataColumn[],
  schemaByColumnIndex: Map<number, DatasetAttributeSchema>,
  useAttributesSchema: boolean
): ResolvedColumn[] => {
  return columns.map((col, i) => {
    if (!useAttributesSchema) {
      return {
        info: col,
        isVisible: !!col?.is_visible,
        isEnhancedGeo: col.is_reference_geo
      };
    }
    return resolveColumnWithSchema(col, schemaByColumnIndex.get(i));
  });
};

/**
 * Processes a single data row, delegating each cell to `processRowValue` and
 * then converting the accumulated result into GeoJSON Feature(s) via
 * `createFeaturesFromRowData`.
 *
 * For each cell in the row:
 * - Non-visible or unmatched columns are skipped via the pre-computed
 *   `resolvedColumns` metadata (no runtime type checks needed).
 * - Geometry columns that were flagged as `isEnhancedGeo` during pre-computation
 *   receive a shallow-copied `columnInfo` with `is_reference_geo: true`, ensuring
 *   `processRowValue` correctly identifies them as geometry. The spread only
 *   happens for geometry columns (typically 1–2 per row), not for every cell.
 *
 * @param rowData               - The raw row values array.
 * @param rowIndex              - The index of this row in the dataset (used for feature ID fallback).
 * @param resolvedColumns       - Pre-computed column metadata array.
 * @param useAttributesSchema   - Whether an `attributesSchema` was provided.
 * @param spatialAggregationType - The dataset's spatial type (e.g. "poi", "adm_area", "h3").
 * @param features              - The output array; new Feature(s) are pushed directly into it.
 */
const processRow = (
  rowData: DatasetDataRowV2,
  rowIndex: number,
  resolvedColumns: ResolvedColumn[],
  useAttributesSchema: boolean,
  spatialAggregationType: SpatialAggregationType,
  features: Array<Feature<Geometry>>
): void => {
  const processedData: ProcessedRowData = {
    properties: {},
    baseId: undefined,
    collectedGeometries: []
  };

  for (let columnIndex = 0; columnIndex < rowData.length; columnIndex++) {
    const resolved = resolvedColumns[columnIndex];
    if (!resolved?.isVisible || !resolved.info) continue;

    const columnInfo =
      useAttributesSchema && resolved.isEnhancedGeo
        ? { ...resolved.info, is_reference_geo: true }
        : resolved.info;

    processRowValue(
      rowData[columnIndex],
      columnInfo,
      useAttributesSchema,
      columnIndex,
      processedData,
      spatialAggregationType
    );
  }

  createFeaturesFromRowData(processedData, rowIndex, features);
};

/**
 * Converts a `DatasetData` response into a GeoJSON `FeatureCollection`.
 *
 * Each row in the dataset becomes one or more GeoJSON Features, where
 * properties are mapped from columns and geometry is parsed from WKT strings.
 *
 * **Schema matching strategy** (when `attributesSchema` is provided):
 *
 * The `columns` array (from the API response) and `attributesSchema` may not
 * be aligned by index — for example, columns can contain internal fields like
 * `orbis_li_id` that have no corresponding schema entry, causing all subsequent
 * indices to shift. To solve this, columns are matched to schema entries by:
 *
 * 1. **Name** – `col.name === schema.attribute_name` (primary, most reliable).
 * 2. **Schema ID** – `col.schema_id === schema.id` (fallback for renamed columns).
 *
 * **Geometry detection**:
 *
 * A column is treated as geometry only when `is_reference_geo` is `true` in
 * either the matched schema entry or the column definition. The `data_type`
 * field is intentionally **not** used as a fallback because some datasets
 * contain secondary geometry columns that are deliberately excluded from
 * the map (`is_reference_geo: false` in both sources).
 *
 * **Performance optimizations**:
 *
 * - Column metadata (visibility, geometry flag) is pre-computed **once** before
 *   the row loop, avoiding repeated Map lookups and type checks per cell.
 * - Object spread for enhanced geometry info only occurs on actual geometry
 *   columns (typically 1–2), not on every cell in every row.
 *
 * @param datasetData       - The dataset response containing columns, rows, and spatial type.
 * @param attributesSchema  - Optional schema array for richer column metadata.
 *                            When omitted, `datasetData.columns` is used directly.
 * @returns A GeoJSON `FeatureCollection` with one Feature per row (or per geometry when
 *          a row contains multiple geometry columns).
 *
 * @example
 * ```typescript
 * // Without schema — uses columns directly
 * const fc = convertDatasetResponseToFeatureCollection(datasetData);
 *
 * // With schema — uses name/schema_id matching + enhanced geo detection
 * const fc = convertDatasetResponseToFeatureCollection(datasetData, schema);
 * ```
 */
export const convertDatasetResponseToFeatureCollection = (
  datasetData: DatasetData,
  attributesSchema?: DatasetAttributeSchema[]
): FeatureCollection<Geometry> => {
  if (!datasetData) return { type: "FeatureCollection", features: [] };

  const { columns, rows, spatial_aggregation_type } = datasetData;
  const useAttributesSchema = !!attributesSchema;
  const features: Array<Feature<Geometry>> = [];

  const schemaByColumnIndex =
    useAttributesSchema && attributesSchema
      ? matchColumnsToSchema(columns, attributesSchema)
      : new Map<number, DatasetAttributeSchema>();

  const resolvedColumns = preComputeColumnMetadata(
    columns,
    schemaByColumnIndex,
    useAttributesSchema
  );

  for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
    processRow(
      rows[rowIndex],
      rowIndex,
      resolvedColumns,
      useAttributesSchema,
      spatial_aggregation_type,
      features
    );
  }

  return { type: "FeatureCollection", features };
};

/**
 * Recursively checks that every position in a coordinates array falls within
 * WGS84 (EPSG:4326) longitude/latitude bounds. Coordinates from a projected
 * CRS (e.g. UTM Zone 48S / EPSG:32748) are expressed in meters, so they land
 * far outside these bounds and are detected here.
 */
const areCoordinatesWithinWGS84Bounds = (coordinates: unknown): boolean => {
  if (!Array.isArray(coordinates)) return true;
  if (typeof coordinates[0] === "number") {
    const [lng, lat] = coordinates as number[];
    return lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
  }
  return coordinates.every(areCoordinatesWithinWGS84Bounds);
};

/**
 * Returns `true` when every geometry in the collection is plausibly WGS84
 * (EPSG:4326) — i.e. all coordinates are within [-180, 180] longitude and
 * [-90, 90] latitude. Used to reject datasets uploaded in a projected CRS
 * (e.g. UTM) before they are painted on the WGS84 map.
 */
export const isFeatureCollectionWGS84 = (
  featureCollection: FeatureCollection<Geometry>
): boolean =>
  featureCollection.features.every(({ geometry }) => {
    if (!geometry) return true;
    if (geometry.type === "GeometryCollection") {
      return geometry.geometries.every((g) =>
        areCoordinatesWithinWGS84Bounds(
          (g as Exclude<Geometry, GeometryCollection>).coordinates
        )
      );
    }
    return areCoordinatesWithinWGS84Bounds(geometry.coordinates);
  });

/**
 * Gets all unique property keys from feature properties
 */
export const getCategoriesFromDatasetReponse = (
  response: DatasetData
): {
  type: DatasetDataType;
  value: string;
}[] => {
  const categories: {
    type: DatasetDataType;
    value: string;
  }[] = [];

  response.columns.forEach((column) => {
    if (
      column.name === "geom" ||
      column.name === "id" ||
      CATCHMENT_GRID_ID_COLUMN_NAME.includes(column.name)
    )
      return;

    categories.push({
      type: column.data_type,
      value: column.name
    });
  });

  return categories;
};

/**
 * Calculates color distribution ranges for feature properties based on their type.
 * For string values, it creates color categories.
 * For numeric values, it creates quantile ranges.
 */
export const calculateFeaturePropertyColorRanges = (
  featureCollection: FeatureCollection<Geometry>,
  propertyName: string,
  isCategory: boolean,
  step: number
) => {
  // Extract property values
  const propertyValues = featureCollection.features.map(
    (f) => f.properties?.[propertyName]
  );

  return isCategory
    ? handleCategoricalValues(propertyValues, step)
    : handleNumericValues(propertyValues, step);
};

/*
 * Transforms properties of a selected feature into a more structured format
 * by associating them with their respective categories.
 * This is useful for displaying properties in a categorized manner.
 */

export const transformPropertiesWithCategory = (
  properties: SelectedFeature,
  categories: DatasetAttributeSchema[],
  timezone?: Timezone
): TransformedProperty[] => {
  return Object.entries(properties).reduce<TransformedProperty[]>(
    (acc, [key, value]) => {
      const category = categories.find((cat) => cat.attribute_name === key);
      if (category && isDatetime(category.data_type)) {
        const datasetDataType = SchemaToDatasetMap[category.data_type];
        const displayValue = translateToUserTimezone(
          timezone,
          datasetDataType,
          value
        );

        acc.push({
          key,
          value: displayValue,
          data_type: category.data_type,
          data_label: category.data_label
        });
      }
      return acc;
    },
    []
  );
};

/**
 * Transforms an array of properties into a key-value pair object.
 * Each property is formatted based on its data type using the `formatRenderValue` function.
 * This transformation is useful for efficiently accessing attributes by their keys
 * and displaying them in a structured format.
 */
export const transformVisibleAttributes = (
  transformProperty: TransformedProperty[],
  timezone?: Timezone
): Record<string, string> => {
  return transformProperty.reduce<Record<string, string>>((acc, item) => {
    const safeValue = item.value ?? "";
    const formattedValue = formatRenderValue({
      type: item.data_type,
      value: safeValue,
      timezone: timezone
    });
    acc[item.key] = String(formattedValue);
    return acc;
  }, {});
};

/** Function to return only the available/not deleted `datasets` from the given `datasets` slice */
export const getAvailableDatasets = (datasets: DatasetsMap): DatasetsMap => {
  return Object.fromEntries(
    Object.entries(datasets).filter(([, dataset]) => !!dataset.data.available)
  );
};

/**
 * Defines matching rules for selecting a default attribute based on spatial aggregation type.
 *
 * Each rule specifies:
 * - @param spatialType: The spatial aggregation type this rule applies to (e.g. H3, POI, polygon)
 * - @param dataType: The expected data type of the matching attribute
 * - @param isMatchingAttribute: A predicate function to further validate the attribute by name/column
 *
 * Rules are evaluated in order and used by `getDefaultSelectedCategory` as priority step 2,
 * after checking for a "score" attribute.
 *
 * Current rules:
 * - H3       → First INT attribute whose name includes "count"
 * - POI      → First TEXT attribute whose column equals "name"
 * - Adm. Area → First TEXT attribute whose column equals "class"
 * - Polygon  → First TEXT attribute whose column equals "class"
 */
const SPATIAL_AGGREGATION_ATTRIBUTE_BY_TYPE: Array<{
  spatialType: string;
  dataType: DataSchemaType;
  isMatchingAttribute: (attr: DatasetAttributeSchema) => boolean;
}> = [
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.H3,
    dataType: DataSchemaType.INT,
    isMatchingAttribute: (attr) =>
      !!attr.attribute_name?.toLowerCase().includes("count")
  },
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.poi.toLowerCase(),
    dataType: DataSchemaType.TEXT,
    isMatchingAttribute: (attr) =>
      attr.attribute_column?.toLowerCase() === "name"
  },
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.adm_area.toLowerCase(),
    dataType: DataSchemaType.TEXT,
    isMatchingAttribute: (attr) =>
      attr.attribute_column?.toLowerCase() === "class"
  },
  {
    spatialType: SPATIAL_TYPE_TO_LAYER_TYPE.polygon.toLowerCase(),
    dataType: DataSchemaType.TEXT,
    isMatchingAttribute: (attr) =>
      attr.attribute_column?.toLowerCase() === "class"
  }
];

/**
 * Finds a suitable attribute from the dataset's attributes based on the spatial aggregation type.
 * This function is used as part of the logic to determine the default selected category for a dataset.
 * It checks the dataset's attributes against predefined rules for specific spatial types (e.g., H3, POI, Polygon) to find an attribute that matches the expected data type and naming conventions.
 * @param attributes - The array of dataset attribute schemas to search through.
 * @param spatialAggregationType - The spatial aggregation type of the dataset, which influences the selection criteria for the attribute.
 * @returns The matching attribute schema if found, otherwise undefined.
 **/
const findSpatialAggregationTypeAttribute = (
  attributes: DatasetAttributeSchema[],
  spatialAggregationType?: SpatialAggregationType
): DatasetAttributeSchema | undefined => {
  const matchedRule = SPATIAL_AGGREGATION_ATTRIBUTE_BY_TYPE.find(
    ({ spatialType }) => spatialType === spatialAggregationType
  );
  return matchedRule
    ? attributes.find(
        (attr) =>
          attr.data_type === matchedRule.dataType &&
          matchedRule.isMatchingAttribute(attr)
      )
    : undefined;
};

/**
 * Selects the most appropriate attribute to use as the default category for dataset visualization.
 *
 * The selection follows a priority order:
 * 1. Prefers the "score" attribute if it exists (case-insensitive)
 * 2. Otherwise, selects the first non-special, non-geometry attribute
 * 3. Falls back to the first attribute in the array
 * 4. Returns undefined if the array is empty
 *
 * @param attributes - Array of dataset attribute schemas to choose from
 * @param spatialAggregationType - The spatial aggregation type of the dataset, which may influence attribute selection for certain types (e.g. H3, POI)
 * @returns The selected attribute schema that should be used as the default category, or undefined if no attributes exist
 *
 * @example
 * ```typescript
 * const attributes = [
 *   { attribute_name: 'id', data_type: DataSchemaType.STRING },
 *   { attribute_name: 'score', data_type: DataSchemaType.NUMERIC },
 *   { attribute_name: 'geometry', data_type: DataSchemaType.GEOMETRY }
 * ];
 *
 * const defaultCategory = getDefaultSelectedCategory(attributes, SPATIAL_AGGREGATION_NAME.H3);
 * // Returns the 'score' attribute
 * ```
 */
export const getDefaultSelectedCategory = (
  attributes: DatasetAttributeSchema[],
  spatialAggregationType?: SpatialAggregationType
): DatasetAttributeSchema | undefined => {
  if (attributes.length === 0) return undefined;

  // 1. Prefer the "score" attribute if it exists
  const scoreAttribute = attributes.find(
    (attr) => attr.attribute_name?.toLowerCase() === "score"
  );
  if (scoreAttribute) return scoreAttribute;

  // 2. Match by spatial type rules (H3 → count, POI → name, adm. area/polygon → class)
  const spatialAggregationTypeAttribute = findSpatialAggregationTypeAttribute(
    attributes,
    spatialAggregationType
  );
  if (spatialAggregationTypeAttribute) return spatialAggregationTypeAttribute;

  // 3. Otherwise, prefer the first non-special, non-geometry attribute
  const regularAttribute = attributes.find(
    (attr) =>
      !(attr.attribute_name in ATTRIBUTE_ID) &&
      attr.data_type !== DataSchemaType.GEOMETRY
  );
  if (regularAttribute) return regularAttribute;

  // 4. Fallback: first attribute
  return attributes[0];
};

/**
 * Determines visibility based on whether it's an analysis result and the dataset name.
 * Only the "spatial analysis result" dataset is visible by default.
 */
export const getDatasetVisibility = (
  isAnalysisResult: boolean,
  datasetName: string | undefined
): { isVisible: boolean; visibility: "visible" | "none" } => {
  const shouldBeVisible =
    isAnalysisResult && datasetName === SPATIAL_ANALYSIS_RESULT_DATASET_NAME;
  return {
    isVisible: shouldBeVisible,
    visibility: shouldBeVisible ? "visible" : "none"
  };
};

/**
 * Creates the default dataset style configuration.
 */
const createDefaultDatasetStyle = (
  defaultCategory: DatasetAttributeSchema | undefined,
  visibility: "visible" | "none"
): Dataset["style"] => ({
  ...defaultDataset.style,
  visibility,
  labelConfig: {
    ...defaultDataset.style.labelConfig,
    attribute_id: defaultCategory?.id ?? "",
    attribute_name: defaultCategory?.attribute_name ?? ""
  },
  stroke: {
    ...defaultDataset.style.stroke,
    attribute_id: defaultCategory?.id ?? "",
    attribute_name: defaultCategory?.attribute_name ?? ""
  }
});

/**
 * Creates a default spatial dataset object with configured properties, styles, and visibility settings.
 *
 * This function constructs a Dataset object from raw dataset data and attributes, applying
 * default configurations and determining visibility based on whether it's an analysis result
 * and the spatial aggregation type.
 *
 * @param {Object} params - The parameters object
 * @param {DatasetData} params.data - The raw dataset data containing id, name, spatial type, and rows
 * @param {DatasetAttributeSchema[]} params.attributes - Array of dataset attribute schemas
 * @param {boolean} [params.isAnalysisResult=false] - Whether this dataset is from an analysis result
 *
 * @returns {Dataset} A configured Dataset object with:
 *   - data: Basic dataset information (id, name, spatial_aggregation_type, total_rows)
 *   - properties: Visibility and fetching state based on analysis result and spatial type
 *   - style: Visibility styling ("visible" or "none") based on spatial aggregation type
 *   - categories: The provided attributes array
 *   - selectedCategory: Default selected category from attributes
 *
 * @example
 * ```typescript
 * const dataset = createDefaultSpatialDataset({
 *   data: {
 *     dataset_id: "123",
 *     name: "Population Data",
 *     spatial_aggregation_type: EOutputAnalysisType.GRID,
 *     rows: [...]
 *   },
 *   attributes: [...],
 *   isAnalysisResult: true
 * });
 * ```
 */
export const createDefaultSpatialDataset = ({
  data,
  attributes,
  isAnalysisResult = false
}: {
  data: DatasetData;
  attributes: DatasetAttributeSchema[];
  isAnalysisResult?: boolean;
}): Dataset => {
  const defaultCategory = getDefaultSelectedCategory(
    attributes,
    data.spatial_aggregation_type
  );
  const { isVisible, visibility } = getDatasetVisibility(
    isAnalysisResult,
    data.name
  );

  return {
    data: {
      ...defaultDataset.data,
      id: data.dataset_id,
      name: data.name,
      display_name: data.display_name ?? data.name,
      spatial_aggregation_type: data.spatial_aggregation_type,
      total_rows: data.rows.length,
      short_description: "",
      dataset_size_kb: data.dataset_size_kb
    },
    properties: {
      isVisible,
      spatialType: data.spatial_aggregation_type,
      isFetching: !isAnalysisResult
    },
    style: createDefaultDatasetStyle(defaultCategory, visibility),
    categories: attributes,
    selectedCategory: defaultCategory ?? DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE
  };
};

export const createSpatialDatasetFromMetadata = ({
  data,
  attributes,
  isAnalysisResult = false,
  h3Configuration = null
}: {
  data: DatasetMetadataStream;
  attributes: DatasetAttributeSchema[];
  isAnalysisResult?: boolean;
  h3Configuration?: H3Configuration | null;
}): Dataset => {
  const defaultCategory = getDefaultSelectedCategory(
    attributes,
    data.spatial_aggregation_type
  );
  const { isVisible, visibility } = getDatasetVisibility(
    isAnalysisResult,
    data.name
  );

  return {
    data: {
      ...defaultDataset.data,
      id: data.dataset_id,
      name: data.name,
      display_name: data.name,
      spatial_aggregation_type: data.spatial_aggregation_type,
      total_rows: data.total_rows,
      short_description: "",
      dataset_size_kb: data.dataset_size_kb
    },
    properties: {
      isVisible,
      spatialType: data.spatial_aggregation_type,
      isFetching: false,
      gridConfig: data.grid_config
    },
    style: createDefaultDatasetStyle(defaultCategory, visibility),
    categories: attributes,
    selectedCategory: defaultCategory ?? DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    h3Configuration
  };
};

/**
 * Checks if a dataset has a 'score' column and if all of its values are null.
 */
export const hasNoScore = (dataset: DatasetData): boolean => {
  const scoreColumnIndex = dataset.columns.findIndex(
    (column) => column.name.toLowerCase() === "score"
  );

  if (scoreColumnIndex === -1) return false;
  if (!dataset.rows?.length) return true;

  return dataset.rows.every((row) => {
    const value = row[scoreColumnIndex];

    if (value === null || value === undefined) return true;

    if (typeof value === "string") return value.trim().length === 0;

    if (typeof value === "object" && value !== null) {
      return Object.values(value).every((v) => {
        if (v === null || v === undefined) return true;
        if (typeof v === "string") return v.trim().length === 0;
        return false;
      });
    }
    return false;
  });
};

/**
 * Util function to check if a given data type is one of the ordinal types.
 * @param dataType - The data type to check against the ordinal types.
 * @returns {boolean} - Returns true if the data type is one of the ordinal types, false otherwise.
 */
export const isOrdinalType = (
  dataType: DataSchemaType | DatasetDataType
): boolean => {
  return (
    ORDINAL_TYPES.includes(dataType) || ORDINAL_TYPES_COLUMN.includes(dataType)
  );
};

/**
 * Derives the default Color Scale method from an attribute's data type:
 * ordinal (categorical) attributes get `"ordinal"`, numeric attributes get
 * `"quantile"`. Shared by dataset creation (`createNewDataset`) and the
 * "Color Based On" default in `DatasetItem` so both stay in sync.
 */
export const getDefaultColorScaleMethod = (
  dataType?: DataSchemaType
): ColorScaleMethod =>
  dataType && isOrdinalType(dataType) ? "ordinal" : "quantile";

export type ColorBasedOnValue = ColorScaleMethod;

type ColorScaleOption = {
  label: string;
  value: ColorScaleMethod;
  /** Which attribute data types this option is applicable to. */
  appliesTo: "numeric" | "non-numeric" | "all";
};

// Labels follow the PRD (Color Scale Option); the values stay as the API expects.
export const COLOR_BASED_ON_OPTIONS: ColorScaleOption[] = [
  { label: "Auto Range", value: "quantile", appliesTo: "numeric" },
  { label: "Category", value: "ordinal", appliesTo: "non-numeric" },
  { label: "Custom Range", value: "custom_breaks", appliesTo: "numeric" },
  { label: "Custom Category", value: "custom_ordinal", appliesTo: "all" },
  { label: "Single Style", value: "simple", appliesTo: "all" }
];

/**
 * Whether a given Color Scale option can be applied to an attribute of the
 * given data type. `isOrdinalType` returns false for numeric types
 * (int/float/percentage/ratio/lat/long) and true for everything else. When
 * `dataType` is omitted, every option is considered applicable.
 */
export const isColorBasedOnApplicable = (
  value: string,
  dataType?: DataSchemaType
): boolean => {
  const option = COLOR_BASED_ON_OPTIONS.find((o) => o.value === value);
  if (!option) return false;
  if (!dataType || option.appliesTo === "all") return true;
  const isNumeric = !isOrdinalType(dataType);
  return option.appliesTo === "numeric" ? isNumeric : !isNumeric;
};

/**
 * The Color Scale options applicable to the given attribute data type,
 * shaped as `{ label, value }` for the shared select component.
 */
export const getColorScaleOptions = (
  dataType?: DataSchemaType
): { label: string; value: string }[] =>
  COLOR_BASED_ON_OPTIONS.filter((option) =>
    isColorBasedOnApplicable(option.value, dataType)
  ).map(({ label, value }) => ({ label, value }));

/**
 * Default "Color Based On" (Color Scale) method, from the two values it needs —
 * the stored method and the attribute data type (not the whole dataset):
 *  1. `storedMethod`, when it still applies to the attribute type. New layers
 *     get it at creation (`createNewDataset` derives "simple" for POI,
 *     ordinal/quantile otherwise) and restored projects keep their saved value —
 *     so new POI correctly defaults to Simple via this branch.
 *  2. otherwise the attribute data type's default (ordinal vs quantile).
 *     Pre-feature projects have no stored method, so they land here and keep
 *     their existing Quantile/Ordinal visuals (PRD: backward compatibility —
 *     do NOT force POI to Simple here, that would mislabel existing projects).
 * Shared by the dataset list (`DatasetItem`) and Preview in Map so both agree.
 */
export const getColorBasedOnDefault = (
  storedMethod: ColorScaleMethod | undefined,
  dataType?: DataSchemaType
): ColorScaleMethod => {
  if (storedMethod && isColorBasedOnApplicable(storedMethod, dataType)) {
    return storedMethod;
  }
  return getDefaultColorScaleMethod(dataType);
};

/**
 * Default "Simple" Color Scale config (flat fill/outline + the attribute name
 * as the legend label). Used both when seeding a new POI dataset and when a
 * layer is switched to Simple without an existing config.
 */
export const getDefaultSingleStyleConfig = (
  label: string
): SingleStyleValue => ({
  fillColor: DEFAULT_SINGLE_STYLE_FILL_COLOR,
  outlineColor: DEFAULT_SINGLE_STYLE_OUTLINE_COLOR,
  label,
  imageUrl: null,
  imageName: null,
  imageAssetId: null,
  iconId: null
});

/** Catch-all "Other" bucket colors for Custom Ordinal (data not assigned). */
export const CUSTOM_CATEGORY_OTHER_FILL_COLOR = "#6b7280";
export const CUSTOM_CATEGORY_OTHER_OUTLINE_COLOR = "#d1d5db";

/** Palette color for the Custom Ordinal row at `index` of `total`, ramping dark →
 * light like the Ordinal scale (Custom Breaks ramps the opposite way). */
export const customCategoryPaletteColor = (
  colorFamily: EColorNames,
  index: number,
  total: number
): string => {
  const scale = getColorScaleMap(colorFamily, Math.max(total, MIN_STEP)) ?? [];
  const key = scale[index] ?? scale[scale.length - 1];
  return COLORS[colorFamily]?.[key] ?? CUSTOM_CATEGORY_OTHER_OUTLINE_COLOR;
};
// Absolute ceiling for Custom Ordinal category steps — matches the Color Scale
// step maximum.
const CUSTOM_CATEGORY_MAX_STEPS = 10;

const clampCustomCategoryStepCount = (stepCount: number): number =>
  Math.min(Math.max(stepCount, 1), CUSTOM_CATEGORY_MAX_STEPS);

/**
 * Category rows to seed: the last value always stays in "Other", so N distinct
 * values give N-1 rows — bounded by the step count and by MAX-1. A single value
 * (or no data yet) has nothing to reserve and follows the step count.
 */
export const getCustomCategoryActiveStepCount = (
  uniqueValuesLength: number,
  stepCount: number
): number => {
  const count = clampCustomCategoryStepCount(stepCount);
  if (uniqueValuesLength <= 1) return count;
  return Math.min(count, uniqueValuesLength - 1, CUSTOM_CATEGORY_MAX_STEPS - 1);
};

/** Unique values of an attribute from the tabular rows, normalized like the map's
 * feature properties so both value spaces stay comparable. */
export const getAttributeTableValues = (
  rawDataset: DatasetData | undefined,
  attributeName: string | undefined
): string[] => {
  if (!attributeName || !rawDataset?.columns) return [];
  const columnIndex = rawDataset.columns.findIndex(
    (col) => col.name === attributeName
  );
  if (columnIndex === -1) return [];
  const dataType = rawDataset.columns[columnIndex].data_type;
  const unique = new Set<string>();
  rawDataset.rows?.forEach((row) => {
    const cell = (row as DatasetDataRowValue[])[columnIndex];
    if (cell !== null && cell !== undefined && cell !== "") {
      unique.add(String(normalizeAttributeValue(cell, dataType)));
    }
  });
  return [...unique];
};

/** A value that falls into no numeric range: null, undefined or empty. */
export const isEmptyAttributeValue = (value: unknown): boolean =>
  value === null || value === undefined || value === "";

/** Whether the attribute has at least one null/empty cell in the loaded table. */
export const hasEmptyAttributeTableValues = (
  rawDataset: DatasetData | undefined,
  attributeName: string | undefined
): boolean => {
  if (!attributeName || !rawDataset?.columns) return false;
  const columnIndex = rawDataset.columns.findIndex(
    (col) => col.name === attributeName
  );
  if (columnIndex === -1) return false;
  return (rawDataset.rows ?? []).some((row) =>
    isEmptyAttributeValue((row as DatasetDataRowValue[])[columnIndex])
  );
};

/**
 * Whether the color ranges carry the "null" bucket, i.e. the attribute held
 * null/empty values when the layer was colored.
 */
export const hasNullColorRange = (
  colorRanges: ColorRanges[] | undefined
): boolean =>
  (colorRanges ?? []).some(
    (range) =>
      range.value === "null" ||
      range.value === "" ||
      String(range.label ?? "").toLowerCase() === "null"
  );

/**
 * Default "Custom Ordinal" config: one category per Color Scale step, each
 * seeded with the matching palette color (fill = outline), plus the catch-all
 * "Other" bucket. Values are auto-generated from the attribute's unique values
 * (one per category, like Ordinal), capped at the step count; the last slot is
 * reserved for the user to add manually, so values beyond the cap fall into
 * "Other" (step 10 → 9 seeded rows). A seeded category is named after its value;
 * empty/padded ones keep the generic "Category N" name. All names stay
 * renamable, and with no `uniqueValues` yet (data not loaded) categories are
 * empty.
 */
export const getDefaultCustomCategoryConfig = (
  colorFamily: EColorNames,
  stepCount: number,
  uniqueValues: string[] = []
): CustomCategoryValue => {
  const count = clampCustomCategoryStepCount(stepCount);

  // Rows to seed: `count` when the data fits, otherwise `count - 1` with the
  // last slot reserved for "Other".
  const seedCount = getCustomCategoryActiveStepCount(
    uniqueValues.length,
    count
  );

  // One distinct value per seeded row, ordered like the Ordinal scale. Sentinels
  // stay in: a real `0` needs its own category, not the "Other" bucket.
  let seededCategoryValues = handleCategoricalValues(uniqueValues, seedCount, {
    dropOrdinalSentinels: false
  }).map((category) => category.category);

  // Defensive: fall back to the raw distinct values if the ordering pass empties.
  if (!seededCategoryValues.length && uniqueValues.length) {
    seededCategoryValues = [...new Set(uniqueValues)].slice(0, seedCount);
  }

  // Row count follows the seeded categories (`seedCount` already reserved the
  // "Other" slot); with no data yet, keep `count` empty placeholder rows.
  const length =
    seededCategoryValues.length > 0 ? seededCategoryValues.length : count;
  const colorAt = (index: number) =>
    customCategoryPaletteColor(colorFamily, index, length);
  const steps: CustomCategoryStep[] = Array.from({ length }, (_, index) => {
    const value = seededCategoryValues[index];
    const color = colorAt(index);
    return {
      // Seeded categories are named after their value (matches the Ordinal
      // legend); padded/empty ones fall back to the generic "Category N".
      label: value ?? `Category ${index + 1}`,
      values: value != null ? [value] : [],
      fillColor: color,
      outlineColor: color,
      imageUrl: null,
      imageName: null,
      imageAssetId: null,
      iconId: null
    };
  });
  return {
    steps,
    otherFillColor: CUSTOM_CATEGORY_OTHER_FILL_COLOR,
    otherOutlineColor: CUSTOM_CATEGORY_OTHER_OUTLINE_COLOR
  };
};

/**
 * Whether a Custom Ordinal config has any assigned values. When it doesn't, the
 * layer/legend should fall back to the default (ordinal) color scale rather than
 * paint everything as "Other" — Custom Ordinal only takes effect once the user
 * assigns values to a category and saves.
 */
export const hasCustomCategoryAssignments = (
  config?: CustomCategoryValue
): boolean => !!config?.steps?.some((step) => step.values.length > 0);

/**
 * Recolors a Custom Ordinal config's category fill/outline from a new Color
 * Scale palette (each step takes the palette color for its index; fill =
 * outline) AND resizes the step count to exactly `stepCount` (PRD Add/Remove
 * semantics): growing pads with empty "Category N" rows, shrinking trims
 * categories from the END. Categories that REMAIN are kept by position with
 * their labels/values/images; trimmed categories are dropped (their values are
 * NOT restored when the count grows again — re-added steps come back empty,
 * matching "Add Step"). The value lookup and "Other" colors are preserved.
 * Used when the palette or step count changes while Custom Ordinal is active.
 */
export const recolorCustomCategoryConfig = (
  config: CustomCategoryValue,
  colorFamily: EColorNames,
  stepCount: number
): CustomCategoryValue => {
  const target = clampCustomCategoryStepCount(stepCount);
  const colorFor = (index: number) =>
    customCategoryPaletteColor(colorFamily, index, target);
  const steps: CustomCategoryStep[] = Array.from(
    { length: target },
    (_, index) => {
      const existing = config.steps[index];
      const color = colorFor(index);
      if (existing) {
        return { ...existing, fillColor: color, outlineColor: color };
      }
      return {
        label: `Category ${index + 1}`,
        values: [],
        fillColor: color,
        outlineColor: color,
        imageUrl: null,
        imageName: null,
        imageAssetId: null,
        iconId: null
      };
    }
  );
  return { ...config, steps };
};

/**
 * Updates a Custom Ordinal config to the filtered data: keeps still-present values
 * and their groupings, and adds one step per still-unassigned value (capped).
 */
export const reconcileCustomCategoryConfig = (
  config: CustomCategoryValue,
  uniqueValues: string[],
  colorFamily: EColorNames,
  /** Active Color Scale step count; defaults to the data cardinality. */
  stepCount: number = uniqueValues.length
): CustomCategoryValue => {
  const available = new Set(uniqueValues);
  // Keep each category's values that still exist; drop categories left empty.
  const survivingSteps = config.steps
    .map((step) => ({
      ...step,
      values: step.values.filter((value) => available.has(value))
    }))
    .filter((step) => step.values.length > 0);
  // Each still-unassigned value becomes its own step, so the step count follows
  // the data. Colors are placeholders — recolorCustomCategoryConfig sets them below.
  const assigned = new Set(survivingSteps.flatMap((step) => step.values));
  const newSteps: CustomCategoryStep[] = uniqueValues
    .filter((value) => !assigned.has(value))
    .map((value) => ({
      label: value,
      values: [value],
      fillColor: "",
      outlineColor: "",
      imageUrl: null,
      imageName: null,
      imageAssetId: null,
      iconId: null
    }));
  // Same reserve rule as the seed: the last value stays in "Other". Trimming from
  // the end keeps the categories the user already had.
  const activeCount = getCustomCategoryActiveStepCount(
    uniqueValues.length,
    stepCount
  );
  const steps = [...survivingSteps, ...newSteps].slice(0, activeCount);
  // Keep at least one (empty) category so the panel/legend always has a row.
  const finalSteps = steps.length
    ? steps
    : [{ ...config.steps[0], values: [] }];
  return recolorCustomCategoryConfig(
    { ...config, steps: finalSteps },
    colorFamily,
    finalSteps.length
  );
};

/** Catch-all "Other" bucket colors for Custom Breaks (values in gaps/outside). */
export const CUSTOM_RANGE_OTHER_FILL_COLOR = "#6b7280";
export const CUSTOM_RANGE_OTHER_OUTLINE_COLOR = "#d1d5db";
export const CUSTOM_RANGE_MAX_STEPS = 10;
export const CUSTOM_RANGE_MIN_STEPS = 2;
/** Custom Breaks accepts at most 3 decimal places (PRD). */
export const CUSTOM_RANGE_DECIMALS = 3;

/**
 * Rounds a break boundary. Integer attributes (e.g. a POI ID) round to whole
 * numbers — the ranges must never show decimals — while float attributes keep up
 * to `CUSTOM_RANGE_DECIMALS` (3) decimal places.
 */
export const roundBreakValue = (
  value: number,
  isInteger: boolean = false
): number => {
  if (isInteger) return Math.round(value);
  const factor = 10 ** CUSTOM_RANGE_DECIMALS;
  return Math.round(value * factor) / factor;
};

/** Whether a numeric attribute holds integers (no decimals allowed in breaks). */
export const isIntegerDataType = (dataType?: DataSchemaType): boolean =>
  dataType === DataSchemaType.INT;

/**
 * A palette color for the break at `index` (0 = the "Less"/min row) in a scale of
 * `total` steps. `COLOR_SCALE_MAP` is ordered dark→light by index. When
 * `reverse` is set the index is flipped so the min row takes the LIGHTEST shade
 * and the max row the darkest; otherwise the min row takes the DARKEST shade.
 */
const breakPaletteColor = (
  colorFamily: EColorNames,
  index: number,
  total: number,
  reverse: boolean
): string => {
  const scale = getColorScaleMap(colorFamily, Math.max(total, 1)) ?? [];
  const position = reverse ? total - 1 - index : index;
  const key = scale[position] ?? scale[reverse ? 0 : scale.length - 1];
  return COLORS[colorFamily]?.[key] ?? CUSTOM_RANGE_OTHER_OUTLINE_COLOR;
};

/**
 * Fill and Outline for a break row, ramped in OPPOSITE directions from MIN→MAX:
 * Fill goes light→dark (matching Quantile — higher value darker), while Stroke
 * goes dark→light. So each row's fill and outline are contrasting shades.
 */
const breakRowColors = (
  colorFamily: EColorNames,
  index: number,
  total: number
): { fillColor: string; outlineColor: string } => ({
  fillColor: breakPaletteColor(colorFamily, index, total, true),
  outlineColor: breakPaletteColor(colorFamily, index, total, false)
});

/**
 * Formats a break boundary for display with thousands separators (e.g.
 * `8,117,993`) — same style as the data table/legend. Integers show no decimals;
 * decimals show up to 3 places. Falls back to the raw string if not formattable.
 */
export const formatBreakValue = (value: number): string => {
  const formatted = formatNumber(value, "decimal");
  return typeof formatted === "string" ? formatted : String(value);
};

/**
 * The numeric range text for a Custom Breaks step (ignores any user label).
 * Open-ended rows read "Less - X" / "Y - More"; middle rows read "X - Y". Values
 * are display-formatted with thousands separators.
 */
export const getCustomRangeText = (
  step: Pick<CustomRangeStep, "start" | "end">
): string => {
  const start = step.start === null ? "Less" : formatBreakValue(step.start);
  const end = step.end === null ? "More" : formatBreakValue(step.end);
  return `${start} - ${end}`;
};

/**
 * The display label for a Custom Breaks range (legend + panel single-line): the
 * user label when set, otherwise the numeric range text. The panel shows BOTH
 * (label above, range below) when a label is set — see `CustomRangeConfig`.
 */
export const getCustomRangeLabel = (
  step: Pick<CustomRangeStep, "label" | "start" | "end">
): string =>
  step.label?.trim() ? step.label.trim() : getCustomRangeText(step);

/**
 * Custom Breaks Fill-panel derivations: whether Custom Breaks is active, its own
 * step count (= break-row count, independent of `colorSteps`), and the dataset's numeric range.
 */
export const getCustomRangeFillConfig = ({
  data,
  isCustomMapVisualizationLegendEnabled,
  stepValue,
  getAttributeNumericRange,
  getAttributeHasEmptyValues
}: {
  data: Dataset;
  isCustomMapVisualizationLegendEnabled: boolean;
  stepValue?: number;
  getAttributeNumericRange: (
    datasetId: string
  ) => { min: number; max: number } | undefined;
  getAttributeHasEmptyValues?: (datasetId: string) => boolean;
}): {
  isCustomRangeFill: boolean;
  customRangeStepCount: number;
  customRangeBounds: { min: number; max: number } | undefined;
  customRangeHasEmptyValues: boolean;
} => {
  const isCustomRangeFill =
    isCustomMapVisualizationLegendEnabled &&
    data.style.colorScaleMethod === "custom_breaks";

  const customRangeStepCount =
    data.style.customRangeConfig?.steps.length ??
    stepValue ??
    data.style.colorSteps;

  const customRangeBounds = isCustomRangeFill
    ? getAttributeNumericRange(data.data.id)
    : undefined;

  const customRangeHasEmptyValues = isCustomRangeFill
    ? Boolean(getAttributeHasEmptyValues?.(data.data.id))
    : false;

  return {
    isCustomRangeFill,
    customRangeStepCount,
    customRangeBounds,
    customRangeHasEmptyValues
  };
};

/**
 * Value + change handler for the Fill panel's step input, following the
 * active color scale method; shared by `DatasetItem` and `MapPreviewMenu`.
 */
export const getFillStepInputConfig = ({
  data,
  isCustomMapVisualizationLegendEnabled,
  stepValue,
  setStepValue,
  getAttributeNumericRange,
  getAttributeHasEmptyValues,
  onChangeCustomCategoryStepCount,
  onChangeCustomRangeStepCount,
  onDefaultStepChange
}: {
  data: Dataset;
  isCustomMapVisualizationLegendEnabled: boolean;
  stepValue?: number;
  setStepValue: (step: number) => void;
  getAttributeNumericRange: (
    datasetId: string
  ) => { min: number; max: number } | undefined;
  getAttributeHasEmptyValues?: (datasetId: string) => boolean;
  onChangeCustomCategoryStepCount: (datasetId: string, step: number) => void;
  onChangeCustomRangeStepCount: (datasetId: string, step: number) => void;
  onDefaultStepChange: (step: number) => void;
}): {
  fillStepConfig: { step: number; onStepChange: (step: number) => void };
  customRangeBounds: { min: number; max: number } | undefined;
  customRangeHasEmptyValues: boolean;
} => {
  const {
    isCustomRangeFill,
    customRangeStepCount,
    customRangeBounds,
    customRangeHasEmptyValues
  } = getCustomRangeFillConfig({
    data,
    isCustomMapVisualizationLegendEnabled,
    stepValue,
    getAttributeNumericRange,
    getAttributeHasEmptyValues
  });

  const isCustomCategoryFill =
    isCustomMapVisualizationLegendEnabled &&
    data.style.colorScaleMethod === "custom_ordinal";

  if (isCustomCategoryFill) {
    return {
      fillStepConfig: {
        // The step this method was last set to; a layer that never set one follows
        // the shared step.
        step:
          data.style.colorScaleSettings?.custom_ordinal?.steps ??
          stepValue ??
          data.style.colorSteps,
        onStepChange: (step: number) => {
          if (step >= MIN_STEP && step <= MAX_STEP) {
            // Keep the input in sync (local override) AND resize the config.
            setStepValue(step);
            onChangeCustomCategoryStepCount(data.data.id, step);
          }
        }
      },
      customRangeBounds,
      customRangeHasEmptyValues
    };
  }

  if (isCustomRangeFill) {
    return {
      fillStepConfig: {
        step: customRangeStepCount,
        onStepChange: (step: number) => {
          if (step >= MIN_STEP && step <= MAX_STEP) {
            onChangeCustomRangeStepCount(data.data.id, step);
          }
        }
      },
      customRangeBounds,
      customRangeHasEmptyValues
    };
  }

  return {
    fillStepConfig: {
      step: stepValue ?? data.style.colorSteps,
      onStepChange: onDefaultStepChange
    },
    customRangeBounds,
    customRangeHasEmptyValues
  };
};

/**
 * Distributes `count` breaks evenly across [min, max] using equal interval
 * (PRD): `interval = (max − min) / count`, boundary[i] = `min + i × interval`.
 * The first row is open-ended "Less" (start `null`), the last "More" (end
 * `null`); Fill ramps light→dark and Stroke dark→light from MIN to MAX (see
 * `breakRowColors`). Break values are always recalculated from the dataset's
 * actual MIN/MAX, never from prior manual edits. For integer attributes the
 * boundaries are whole numbers (no decimals).
 */
export const redistributeCustomRange = (
  count: number,
  min: number,
  max: number,
  colorFamily: EColorNames,
  isInteger: boolean = false
): CustomRangeStep[] => {
  const total = Math.min(
    Math.max(count, CUSTOM_RANGE_MIN_STEPS),
    CUSTOM_RANGE_MAX_STEPS
  );
  const interval = (max - min) / total;
  const boundary = (i: number) =>
    roundBreakValue(min + i * interval, isInteger);
  return Array.from({ length: total }, (_, index) => {
    const start = index === 0 ? null : boundary(index);
    const end = index === total - 1 ? null : boundary(index + 1);
    const { fillColor, outlineColor } = breakRowColors(
      colorFamily,
      index,
      total
    );
    return {
      label: "",
      start,
      end,
      fillColor,
      outlineColor,
      imageUrl: null,
      imageName: null,
      imageAssetId: null,
      iconId: null
    };
  });
};

/**
 * The default "Custom Breaks" config seeded when a layer first switches to the
 * scale: `count` equal-interval ranges across the dataset MIN/MAX plus the
 * catch-all "Other" bucket. Inherited step count comes from the previous Color
 * Scale (Quantile) setting (PRD).
 */
export const getDefaultCustomRangeConfig = (
  colorFamily: EColorNames,
  count: number,
  min: number,
  max: number,
  isInteger: boolean = false
): CustomRangeValue => ({
  steps: redistributeCustomRange(count, min, max, colorFamily, isInteger),
  otherFillColor: CUSTOM_RANGE_OTHER_FILL_COLOR,
  otherOutlineColor: CUSTOM_RANGE_OTHER_OUTLINE_COLOR
});

/**
 * Recolors a Custom Breaks config's fill/outline from a new Color Scale palette
 * — Fill light→dark and Stroke dark→light from MIN to MAX (see `breakRowColors`)
 * — WITHOUT touching the break values, labels or images. Used on a palette change
 * while Custom Breaks is active (unlike a step-count change, which redistributes
 * the values from scratch).
 */
export const recolorCustomRangeConfig = (
  config: CustomRangeValue,
  colorFamily: EColorNames
): CustomRangeValue => {
  const total = config.steps.length || CUSTOM_RANGE_MIN_STEPS;
  return {
    ...config,
    steps: config.steps.map((step, index) => ({
      ...step,
      ...breakRowColors(colorFamily, index, total)
    }))
  };
};

/** Whether a value falls inside a break range: `> start` (or any) and `≤ end`. */
export const isValueInBreak = (
  value: number,
  step: Pick<CustomRangeStep, "start" | "end">
): boolean =>
  (step.start === null || value > step.start) &&
  (step.end === null || value <= step.end);

/**
 * Numeric [min, max] of a set of raw values (strings/numbers), skipping empty /
 * non-numeric entries. Returns `null` when there is no usable numeric spread.
 */
export const getNumericRange = (
  values: (number | string | null | undefined)[]
): { min: number; max: number } | null => {
  let min = Infinity;
  let max = -Infinity;
  values.forEach((raw) => {
    if (raw === null || raw === undefined || raw === "") return;
    const n = typeof raw === "number" ? raw : Number(raw);
    if (!Number.isFinite(n)) return;
    if (n < min) min = n;
    if (n > max) max = n;
  });
  return Number.isFinite(min) && Number.isFinite(max) && max > min
    ? { min, max }
    : null;
};

/**
 * Coerces a color-range bound to a finite number. Quantile ranges may store
 * `min`/`max` as formatted STRINGS (e.g. the last range's `min` from a quantile
 * boundary), so a strict `typeof === "number"` check drops them — coerce instead.
 * Returns `null` for empty / null / non-numeric.
 */
const toFiniteBound = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Falls back to the numeric spread already computed for the Quantile color
 * ranges when live feature values aren't available — the smallest `min` and the
 * largest `max` across the numeric ranges bound the dataset. Values are coerced
 * from strings, so string-formatted quantile bounds still resolve.
 */
export const getColorRangesMinMax = (
  colorRanges: ColorRanges[]
): { min: number; max: number } | null => {
  const mins: number[] = [];
  const maxes: number[] = [];
  colorRanges.forEach((range) => {
    const min = toFiniteBound(range.min);
    const max = toFiniteBound(range.max);
    if (min !== null && max !== null) {
      mins.push(min);
      maxes.push(max);
    }
  });
  if (!mins.length) return null;
  const min = Math.min(...mins);
  const max = Math.max(...maxes);
  return max > min ? { min, max } : null;
};

export const generateFilterableAttributes = <T extends DatasetDataColumn>(
  columns: T[] | undefined | null
) => {
  if (!columns || columns.length === 0) {
    return [];
  }

  return columns
    .filter((col) => col.is_visible)
    .map((col) => ({
      label: col.name,
      value: normalizeFilterValue(col.name),
      type: col.data_type,
      code: normalizeFilterValue(col.code)
    }));
};

export const formatAddAndUpdateDatasetRow = (
  data: Record<string, unknown>,
  metadata?: DatasetMetadata
): AddUpdateRowsDatasetPayload[] => {
  if (!metadata) return [];

  const allowedNames = new Set(metadata.schema.map((m) => m.code));

  const mappedData = Object.entries(data).map(([key, value]) => {
    const schema = metadata.schema.find((s) => s.code === key);
    // Handle date/time types
    if (
      schema?.data_type === DataSchemaType.DATE ||
      schema?.data_type === DataSchemaType.DATE_TIME ||
      schema?.data_type === DataSchemaType.TIME
    ) {
      const rawValue = value as string;
      return {
        code: key,
        name: key,
        // An empty optional date/time field must be sent as "" (the column is
        // nullable). Formatting an empty value with dayjs yields "Invalid Date",
        // so short-circuit before converting.
        value: rawValue
          ? convertFormatterDayjs(
              rawValue,
              schema?.time_format || "",
              schema.data_type
            )
          : ""
      };
    }

    // Handle geometry types
    if (schema?.data_type === DataSchemaType.GEOMETRY) {
      const format = data[`format_geom_${key}`];

      if (format === "coordinates") {
        const latitude = data[`latitude_geom_input_${key}`];
        const longitude = data[`longitude_geom_input_${key}`];
        return {
          code: key,
          name: key,
          value: `POINT(${longitude} ${latitude})`
        };
      }

      if (format === "wkt") {
        return {
          code: key,
          name: key,
          value: data[`wkt_geom_input_${key}`] as string
        };
      }

      if (format === "geojson") {
        return {
          code: key,
          name: key,
          value: data[`geojson_geom_input_${key}`] as string
        };
      }

      if (format === "draw_polygon") {
        return {
          code: key,
          name: key,
          value: data[`draw_polygon_${key}`] as string
        };
      }

      return {
        code: key,
        name: key,
        value: value as string
      };
    }

    // Default (stringify all other types)
    return {
      code: key,
      name: key,
      value: String(value)
    };
  });

  return mappedData.filter((item) => allowedNames.has(item.code));
};

/**
 * Creates dataset attributes from column data
 * @param columns - Array of column data from dataset
 * @returns Array of DatasetAttributeSchema objects
 */
export const createDatasetAttributes = (
  columns: DatasetData["columns"]
): DatasetAttributeSchema[] => {
  return columns.map((column, index) => ({
    ...DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    id: index.toString(),
    code: column.code,
    attribute_column: column.code,
    attribute_name: column.name,
    data_type: DATA_TYPE_VALUES[column.data_type],
    is_nullable: false,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: index,
    is_visible: column?.is_visible ?? false,
    data_label: column?.data_label ?? undefined
  }));
};

/** Every Color Scale method, so per-method defaults can be seeded for all of them. */
const COLOR_SCALE_METHODS: ColorScaleMethod[] = [
  "simple",
  "ordinal",
  "quantile",
  "custom_ordinal",
  "custom_breaks"
];

export const createNewDataset = ({
  dataset,
  datasetId,
  datasetName,
  attributesSchema,
  h3Configuration = null,
  styleConfig,
  totalRows = 0,
  index,
  group_by,
  is_global_admin_area = false,
  showCatchmentPopup = false
}: CreateNewDatasetType): Dataset => {
  const defaultCategory = getDefaultSelectedCategory(
    attributesSchema,
    dataset.spatial_aggregation_type
  );
  const colors =
    EColorNames[ALL_COLORS[index || 0] as keyof typeof EColorNames];
  const defaultColorSteps = computeStepValue(totalRows);
  // Default Color Scale method: POI datasets default to "Simple"; other layers
  // derive from the attribute data type (ordinal vs quantile). An explicit
  // `styleConfig.colorScaleMethod` (e.g. a restored/saved value) still wins.
  const derivedColorScaleMethod: ColorScaleMethod =
    dataset.spatial_aggregation_type === "poi"
      ? "simple"
      : getDefaultColorScaleMethod(defaultCategory?.data_type);
  const colorScaleMethod =
    styleConfig?.colorScaleMethod ?? derivedColorScaleMethod;

  // First default color family per color based on
  const defaultColorFamily = styleConfig?.colorFamily ?? colors;
  const defaultOpacity = styleConfig?.opacity ?? 0.7;
  // Every method starts on the layer's palette and opacity, so a method never
  // inherits another's. A restored config then overrides what it was saved with.
  const colorScaleSettings: Partial<
    Record<ColorScaleMethod, ColorScaleSettings>
  > = Object.fromEntries(
    COLOR_SCALE_METHODS.map((method) => [
      method,
      {
        colorFamily: defaultColorFamily,
        opacity: defaultOpacity,
        ...styleConfig?.colorScaleSettings?.[method]
      }
    ])
  );
  // Prefer a restored config (from a saved project/analysis config); otherwise
  // seed a default Simple config so a new POI layer renders on the map/legend
  // immediately (matching the config panel default) without needing a Save.
  const singleStyleConfig: SingleStyleValue | undefined =
    styleConfig?.singleStyleConfig ??
    (colorScaleMethod === "simple"
      ? getDefaultSingleStyleConfig(defaultCategory?.attribute_name ?? "")
      : undefined);
  const isCatchmentPolygon =
    showCatchmentPopup && dataset.spatial_aggregation_type === "polygon";
  return {
    data: {
      ...dataset,
      id: datasetId,
      name: dataset.name,
      display_name: datasetName,
      schema: dataset.schema,
      available: dataset.available ?? true
    },
    properties: {
      isVisible: dataset.available ?? true,
      spatialType: dataset.spatial_aggregation_type
    },
    style: {
      weight: 1,
      opacity: 0.7,
      color: "#fff",
      fillColor: COLORS[defaultColorFamily][500],
      colorFamily: defaultColorFamily,
      colorScaleSettings,
      fillOpacity: styleConfig?.opacity ?? 0.7,
      visibility: "visible",
      colorRanges: [],
      labelConfig: {
        color_scale:
          styleConfig?.labelConfig?.color_scale ?? EColorLabel.BLACK_WHITE,
        attribute_id:
          styleConfig?.labelConfig?.attribute_id ?? defaultCategory?.id ?? "",
        attribute_name:
          attributesSchema.find(
            (item) => item.id === styleConfig?.labelConfig?.attribute_id
          )?.attribute_name ??
          defaultCategory?.attribute_name ??
          "",
        show_label:
          (styleConfig?.labelConfig?.show_label || isCatchmentPolygon) ?? false,
        opacity: styleConfig?.labelConfig?.opacity ?? 0.7
      },
      rankConfig: {
        isEnabled: styleConfig?.rankConfig?.isEnabled ?? false,
        maxRanking: styleConfig?.rankConfig?.maxRanking ?? 10
      },
      stroke: {
        attribute_id:
          styleConfig?.strokeConfig?.attribute_id ?? defaultCategory?.id ?? "",
        attribute_name:
          styleConfig?.strokeConfig?.attribute_name ??
          defaultCategory?.attribute_name ??
          "",
        width: styleConfig?.strokeConfig?.width ?? 1,
        opacity: styleConfig?.strokeConfig?.opacity ?? 0.7
      },
      colorSteps: styleConfig?.colorSteps ?? defaultColorSteps,
      colorScaleMethod,
      // A restored config is by definition user-saved, so it hydrates BOTH the
      // live and saved slots (the saved slot is the Cancel baseline + "confirmed"
      // marker). A fresh dataset seeds these lazily on method switch.
      singleStyleConfig,
      singleStyleConfigSaved: singleStyleConfig,
      // Restored "Custom Ordinal" config, when opening a saved config; a fresh
      // dataset seeds this lazily when the user switches to the method. A restored
      // config hydrates BOTH slots (the saved slot is the Cancel baseline).
      customCategoryConfig: styleConfig?.customCategoryConfig,
      customCategoryConfigSaved: styleConfig?.customCategoryConfig,
      // Restored "Custom Breaks" config, when opening a saved config; a fresh
      // dataset seeds this lazily when the user switches to the method. A restored
      // config hydrates BOTH slots (the saved slot is the Cancel baseline).
      customRangeConfig: styleConfig?.customRangeConfig,
      customRangeConfigSaved: styleConfig?.customRangeConfig
    },
    categories: attributesSchema,
    selectedCategory:
      styleConfig?.selectedCategory ??
      defaultCategory ??
      DATASET_ATTRIBUTE_SCHEMA_DEFAULT_VALUE,
    h3Configuration,
    group_by,
    is_global_admin_area
  };
};

export function buildGridConfig(h3Configuration?: H3Configuration | null) {
  if (!h3Configuration) return {};

  return {
    grid_config: {
      level: h3Configuration.resolution
        ? Number(h3Configuration.resolution)
        : undefined,
      aggregation_type: h3Configuration.aggregationType
    }
  };
}

export function sumPolygonDatasetArea(
  datasets: CatchmentDatasetConfig["datasets"]
): number {
  let totalArea = 0;
  for (const entry of Object.values(datasets)) {
    if (entry.dataset?.data?.spatial_aggregation_type !== "polygon") continue;
    for (const feature of convertDatasetResponseToFeatureCollection(
      entry.rawDataset
    ).features) {
      if (
        feature.geometry?.type === "Polygon" ||
        feature.geometry?.type === "MultiPolygon"
      ) {
        totalArea += area(feature);
      }
    }
  }
  return totalArea;
}

/**
 * Color conversion helpers shared by the Color Picker panel (Hex/RGB/HSL/HSB
 * tabs). All hex values are `#rrggbb` lowercase. HSL/HSV use degrees (0-360)
 * for hue and percentages (0-100) for the remaining channels.
 */

export type Rgb = { r: number; g: number; b: number };
export type Hsl = { h: number; s: number; l: number };
/** HSB is the same model as HSV (Brightness === Value). */
export type Hsv = { h: number; s: number; v: number };

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

/** Normalize a hex string to a `#rrggbb` lowercase form (expands shorthand). */
export const normalizeHex = (hex: string): string => {
  let h = hex.trim().replace(/^#/, "");
  if (h.length === 3) {
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  }
  return `#${h.toLowerCase()}`;
};

export const hexToRgb = (hex: string): Rgb | null => {
  const h = normalizeHex(hex).replace(/^#/, "");
  if (h.length !== 6 || /[^0-9a-f]/.test(h)) return null;
  const num = Number.parseInt(h, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

export const rgbToHex = ({ r, g, b }: Rgb): string => {
  const toHex = (v: number) =>
    clamp(Math.round(v || 0), 0, 255)
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const rgbToHsl = ({ r, g, b }: Rgb): Hsl => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
};

export const hslToRgb = ({ h, s, l }: Hsl): Rgb => {
  const sn = clamp(s, 0, 100) / 100;
  const ln = clamp(l, 0, 100) / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = ln - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp >= 0 && hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  };
};

export const rgbToHsv = ({ r, g, b }: Rgb): Hsv => {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    switch (max) {
      case rn:
        h = ((gn - bn) / delta) % 6;
        break;
      case gn:
        h = (bn - rn) / delta + 2;
        break;
      default:
        h = (rn - gn) / delta + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : delta / max;
  return { h: Math.round(h), s: Math.round(s * 100), v: Math.round(max * 100) };
};

export const hsvToRgb = ({ h, s, v }: Hsv): Rgb => {
  const sn = clamp(s, 0, 100) / 100;
  const vn = clamp(v, 0, 100) / 100;
  const c = vn * sn;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = vn - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (hp >= 0 && hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255)
  };
};
